import { useEffect, useState, useMemo } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useLang } from "@/i18n/LanguageContext";
import SeoHead from "@/components/SeoHead";
import PageHero from "@/components/PageHero";
import { Search as SearchIcon, ArrowRight } from "lucide-react";
import { TOPICS } from "@/lib/topics";
import { pathFromKind } from "@/lib/ecosystem";
import { articlePath } from "@/lib/radarPaths";

const CONTENT_TYPES_OPTS = [
  { v: "news", labelEn: "News", labelFr: "Actualités" },
  { v: "jobs", labelEn: "Jobs", labelFr: "Recrutements" },
  { v: "alerts", labelEn: "Alerts", labelFr: "Signaux" },
  { v: "insights", labelEn: "Insights", labelFr: "Analyses" },
  { v: "tutorials", labelEn: "Tutorials", labelFr: "Tutoriels" },
  { v: "vendors", labelEn: "Vendors", labelFr: "Éditeurs" },
  { v: "service_providers", labelEn: "Service providers", labelFr: "Cabinets" },
  { v: "experts", labelEn: "Experts", labelFr: "Experts" },
];

const Search = () => {
  const { t, lang } = useLang();
  const [params, setParams] = useSearchParams();
  const initial = params.get("q") || "";
  const initialTag = params.get("tag") || "";
  const initialCategory = params.get("category") || "";
  const initialTypes = params.get("types") || "";
  const initialSince = params.get("since") || "";

  const [q, setQ] = useState(initial);
  const [tag, setTag] = useState(initialTag);
  const [category, setCategory] = useState(initialCategory);
  const [since, setSince] = useState(initialSince);
  const [typesSel, setTypesSel] = useState(new Set(initialTypes ? initialTypes.split(",") : []));
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [retryIn, setRetryIn] = useState(0);

  const placeholder = useMemo(
    () => (lang === "fr" ? "Rechercher news, jobs, signaux, analyses, écosystème…" : "Search news, jobs, alerts, insights, ecosystem…"),
    [lang],
  );

  useEffect(() => {
    if (!initial && !initialTag) return;
    setLoading(true);
    const apiParams = { q: initial || initialTag || "", language: lang };
    if (initialTag) apiParams.tag = initialTag;
    if (initialCategory) apiParams.category = initialCategory;
    if (initialTypes) apiParams.types = initialTypes;
    if (initialSince) apiParams.since = initialSince;
    api.get(`/search`, { params: apiParams })
      .then((r) => { setResults(r.data); setRetryIn(0); })
      .catch((err) => {
        if (err.response?.status !== 429) return;
        setResults(null);
        setRetryIn(Number(err.response.headers?.["retry-after"]) || 30);
      })
      .finally(() => setLoading(false));
  }, [initial, initialTag, initialCategory, initialTypes, initialSince, lang]);

  const submit = (e) => {
    e.preventDefault();
    const np = {};
    if (q) np.q = q;
    if (tag) np.tag = tag;
    if (category) np.category = category;
    if (since) np.since = since;
    if (typesSel.size > 0) np.types = Array.from(typesSel).join(",");
    setParams(np);
  };

  const resetFilters = () => {
    setQ("");
    setTag("");
    setCategory("");
    setSince("");
    setTypesSel(new Set());
    setParams({});
    setResults(null);
  };

  const toggleType = (v) => {
    const next = new Set(typesSel);
    if (next.has(v)) next.delete(v); else next.add(v);
    setTypesSel(next);
  };

  const total = results
    ? ["news", "jobs", "alerts", "insights", "tutorials", "vendors", "service_providers", "experts"]
        .reduce((s, k) => s + (results[k]?.length || 0), 0)
    : 0;

  return (
    <div data-testid="search-page">
      <SeoHead
        lang={lang}
        path="/search"
        title={lang === "fr" ? "Rechercher dans le radar" : "Search the radar"}
        description={lang === "fr"
          ? "Rechercher news, jobs, alerts, insights, tutoriels et écosystème dans le radar Dataleo."
          : "Search news, jobs, alerts, insights, tutorials and ecosystem across the Dataleo radar."}
        noindex
      />
      <PageHero
        kicker={lang === "fr" ? "Recherche" : "Search"}
        title={lang === "fr" ? "Rechercher dans le radar" : "Search the radar"}
        description={lang === "fr"
          ? "News, jobs, alertes, analyses, tutoriels, écosystème - tout Dataleo Radar."
          : "News, jobs, alerts, insights, tutorials, ecosystem - the whole Dataleo Radar."}
        accent="green"
        compact
        testId="search-hero"
      />
      <div className="dl-container py-10">

      <form onSubmit={submit} className="space-y-4">
        <div className="flex items-stretch border-2 border-[#111827] bg-white max-w-2xl">
          <span className="grid place-items-center px-4 bg-[#111827] text-white">
            <SearchIcon size={18} />
          </span>
          <input
            data-testid="search-input"
            autoFocus
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={placeholder}
            className="flex-1 outline-none px-3 py-3 text-[15px] text-[#111827]"
          />
          <button
            data-testid="search-submit"
            type="submit"
            className="bg-[#00A878] hover:bg-[#008a62] text-white px-5 inline-flex items-center gap-1.5 text-sm font-medium"
          >
            {lang === "fr" ? "Chercher" : "Search"} <ArrowRight size={14} />
          </button>
        </div>

        <div className="flex flex-wrap items-end gap-4">
          <label className="flex flex-col gap-1 text-[12px]">
            <span className="dl-overline">{lang === "fr" ? "Catégorie" : "Category"}</span>
            <select
              data-testid="search-filter-category"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="bg-white border border-[#E5E7EB] px-3 py-2 text-sm focus:outline-none focus:border-[#111827]"
            >
              <option value="">{lang === "fr" ? "Toutes" : "All"}</option>
              {TOPICS.map((tp) => (
                <option key={tp.slug} value={tp.en}>{lang === "fr" ? tp.fr : tp.en}</option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1 text-[12px]">
            <span className="dl-overline">Tag</span>
            <input
              data-testid="search-filter-tag"
              type="text"
              value={tag}
              onChange={(e) => setTag(e.target.value)}
              placeholder={lang === "fr" ? "ex. o9" : "e.g. o9"}
              className="bg-white border border-[#E5E7EB] px-3 py-2 text-sm focus:outline-none focus:border-[#111827]"
            />
          </label>
          <label className="flex flex-col gap-1 text-[12px]">
            <span className="dl-overline">{t.filters.period}</span>
            <select
              data-testid="search-filter-period"
              value={since}
              onChange={(e) => setSince(e.target.value)}
              className="bg-white border border-[#E5E7EB] px-3 py-2 text-sm focus:outline-none focus:border-[#111827]"
            >
              <option value="">{t.filters.periodAll}</option>
              <option value="today">{t.filters.periodToday}</option>
              <option value="week">{t.filters.periodWeek}</option>
              <option value="month">{t.filters.periodMonth}</option>
              <option value="quarter">{t.filters.periodQuarter}</option>
              <option value="year">{t.filters.periodYear}</option>
            </select>
          </label>
          <button
            type="button"
            data-testid="filter-reset"
            onClick={resetFilters}
            className="text-sm underline-offset-4 hover:underline text-[#4B5563] self-end pb-2"
          >
            {t.filters.reset}
          </button>
        </div>

        <div className="flex flex-wrap gap-2">
          <span className="dl-overline mr-2 self-center">{lang === "fr" ? "Types" : "Types"}</span>
          {CONTENT_TYPES_OPTS.map((opt) => {
            const active = typesSel.has(opt.v);
            return (
              <button
                type="button"
                key={opt.v}
                data-testid={`search-type-${opt.v}`}
                onClick={() => toggleType(opt.v)}
                className={`px-2.5 py-1 text-[11px] font-mono-dl uppercase tracking-wider border ${active ? "bg-[#111827] text-white border-[#111827]" : "bg-white text-[#4B5563] border-[#E5E7EB] hover:border-[#111827]"}`}
              >
                {lang === "fr" ? opt.labelFr : opt.labelEn}
              </button>
            );
          })}
          {typesSel.size > 0 && (
            <button
              type="button"
              data-testid="search-type-clear"
              onClick={() => setTypesSel(new Set())}
              className="text-[11px] text-[#4B5563] underline-offset-4 hover:underline self-center ml-2"
            >
              {lang === "fr" ? "Effacer" : "Clear"}
            </button>
          )}
        </div>
      </form>

      {loading && <div className="mt-10 text-[#4B5563]">…</div>}
      {!loading && retryIn > 0 && (
        <div data-testid="search-rate-limited" className="mt-10 border-l-4 border-[#FFD400] bg-[#FFFBEB] px-4 py-3 text-[14px] text-[#111827]">
          {lang === "fr"
            ? `Trop de recherches en peu de temps. Réessayez dans ${retryIn} secondes.`
            : `Too many searches in a short time. Please try again in ${retryIn} seconds.`}
        </div>
      )}

      {!loading && results && (
        <div className="mt-8">
          <div className="font-mono-dl text-[11px] text-[#4B5563] uppercase tracking-wider mb-6">
            {total} {lang === "fr" ? "résultats" : "results"}
            {initial && <> {lang === "fr" ? "pour" : "for"} "<span className="text-[#111827]">{initial}</span>"</>}
            {initialTag && <> · #{initialTag}</>}
            {initialCategory && <> · {initialCategory}</>}
          </div>

          {results.news?.length > 0 && (
            <Section title={t.nav.news} testid="search-news">
              {results.news.map((n) => (
                <Link key={n.id} to={articlePath("news", n)} className="block py-3 border-b border-[#E5E7EB] group" data-testid={`search-news-${n.id}`}>
                  <div className="font-mono-dl text-[10px] uppercase tracking-wider text-[#4B5563]">{n.category || n.relevance_area} · {n.published_at}</div>
                  <div className="text-[16px] font-display group-hover:text-[#00A878]">{n.title}</div>
                </Link>
              ))}
            </Section>
          )}

          {results.jobs?.length > 0 && (
            <Section title={t.nav.jobs} testid="search-jobs">
              {results.jobs.map((j) => (
                <Link key={j.id} to={articlePath("jobs", j)} className="block py-3 border-b border-[#E5E7EB] group" data-testid={`search-job-${j.id}`}>
                  <div className="font-mono-dl text-[10px] uppercase tracking-wider text-[#4B5563]">{j.company} · {j.location}</div>
                  <div className="text-[16px] font-display group-hover:text-[#00A878]">{j.title}</div>
                </Link>
              ))}
            </Section>
          )}

          {results.alerts?.length > 0 && (
            <Section title={t.nav.alerts} testid="search-alerts">
              {results.alerts.map((a) => (
                <Link key={a.id} to={articlePath("alerts", a)} className="block py-3 border-b border-[#E5E7EB] group" data-testid={`search-alert-${a.id}`}>
                  <div className="font-mono-dl text-[10px] uppercase tracking-wider text-[#4B5563]">{a.severity} · {a.published_at}</div>
                  <div className="text-[16px] font-display group-hover:text-[#00A878]">{a.title}</div>
                </Link>
              ))}
            </Section>
          )}

          {results.insights?.length > 0 && (
            <Section title={t.nav.insights} testid="search-insights">
              {results.insights.map((i) => (
                <Link key={i.id} to={articlePath("insights", i)} className="block py-3 border-b border-[#E5E7EB] group" data-testid={`search-insight-${i.id}`}>
                  <div className="font-mono-dl text-[10px] uppercase tracking-wider text-[#4B5563]">Dataleo Insight · {i.published_at}</div>
                  <div className="text-[16px] font-display group-hover:text-[#00A878]">{i.title}</div>
                </Link>
              ))}
            </Section>
          )}

          {results.tutorials?.length > 0 && (
            <Section title={t.nav.tutorials} testid="search-tutorials">
              {results.tutorials.map((tu) => (
                <Link key={tu.id} to={articlePath("tutorials", tu)} className="block py-3 border-b border-[#E5E7EB] group" data-testid={`search-tutorial-${tu.id}`}>
                  <div className="font-mono-dl text-[10px] uppercase tracking-wider text-[#4B5563]">{tu.difficulty} · {tu.published_at}</div>
                  <div className="text-[16px] font-display group-hover:text-[#00A878]">{tu.title}</div>
                </Link>
              ))}
            </Section>
          )}

          {results.vendors?.length > 0 && (
            <Section title={lang === "fr" ? "Éditeurs" : "Vendors"} testid="search-vendors">
              {results.vendors.map((e) => (
                <Link key={e.id} to={`/radar/ecosystem/${pathFromKind(e.kind)}/${e.slug}`} className="block py-3 border-b border-[#E5E7EB] group" data-testid={`search-vendor-${e.id}`}>
                  <div className="font-mono-dl text-[10px] uppercase tracking-wider text-[#00A878]">{lang === "fr" ? "Éditeur" : "Vendor"}</div>
                  <div className="text-[16px] font-display group-hover:text-[#00A878]">{e.name}</div>
                </Link>
              ))}
            </Section>
          )}

          {results.service_providers?.length > 0 && (
            <Section title={lang === "fr" ? "Cabinets" : "Service providers"} testid="search-providers">
              {results.service_providers.map((e) => (
                <Link key={e.id} to={`/radar/ecosystem/${pathFromKind(e.kind)}/${e.slug}`} className="block py-3 border-b border-[#E5E7EB] group" data-testid={`search-provider-${e.id}`}>
                  <div className="font-mono-dl text-[10px] uppercase tracking-wider text-[#00A878]">{lang === "fr" ? "Cabinet" : "Service provider"}</div>
                  <div className="text-[16px] font-display group-hover:text-[#00A878]">{e.name}</div>
                </Link>
              ))}
            </Section>
          )}

          {results.experts?.length > 0 && (
            <Section title="Experts" testid="search-experts">
              {results.experts.map((e) => (
                <Link key={e.id} to={`/radar/ecosystem/${pathFromKind(e.kind)}/${e.slug}`} className="block py-3 border-b border-[#E5E7EB] group" data-testid={`search-expert-${e.id}`}>
                  <div className="font-mono-dl text-[10px] uppercase tracking-wider text-[#00A878]">Expert</div>
                  <div className="text-[16px] font-display group-hover:text-[#00A878]">{e.name}</div>
                </Link>
              ))}
            </Section>
          )}

          {total === 0 && (
            <div data-testid="search-empty" className="text-[#4B5563] py-10 text-center">
              {lang === "fr" ? "Aucun résultat." : "No results."}
            </div>
          )}
        </div>
      )}
      </div>
    </div>
  );
};

const Section = ({ title, children, testid }) => (
  <div className="mb-8" data-testid={testid}>
    <div className="font-mono-dl text-[10px] uppercase tracking-[0.22em] text-[#00A878] mb-2 border-b-2 border-[#111827] pb-2">{title}</div>
    {children}
  </div>
);

export default Search;
